import React, { useContext } from 'react';
import {
  CategoryColor,
  GetCategoriesUsageLazyQueryHookResult,
  PostStatsByCategory,
  UpdateCategoryMutation
} from '../../../../../models/graphql';
import { CategoryTableView } from './CategoryTable.view';
import { CategoriesColorsEnum } from '../../../../../constants/categories-colors';
import { AnalyticsContext } from '../../../AnalyticsProvider';
import { getAvailableMetrics } from '../../helpers/chartHelpers';

type CategoryTableControllerProps = {
  data: GetCategoriesUsageLazyQueryHookResult[1]['data'];
};

type CategoriesResult =
  | Array<{
      __typename?: 'Category';
      id: string;
      name: string;
      color: CategoryColor;
      defaultSocialProfiles: UpdateCategoryMutation['updateCategory']['defaultSocialProfiles'];
    }>
  | undefined;

type CategoryData = {
  categoryId: string;
  postCount: number | null | undefined;
  postPercentage: number | null | undefined;
  likeCount: number | null | undefined;
  likePercentage: number | null | undefined;
  shareCount: number | null | undefined;
  sharePercentage: number | null | undefined;
  commentsCount: number | null | undefined;
  commentsPercentage: number | null | undefined;
};

export type CategoriesUsageData = CategoryData & {
  name: string;
  color: keyof typeof CategoriesColorsEnum;
};

const normalizedCategories = (categories: PostStatsByCategory[] | null | undefined): CategoryData[] => {
  if (!categories) {
    return [];
  }
  const basePostCount = categories[0].postStats[0].postCount;
  const baseLikeCount = Math.max(
    ...categories.map(categoryData =>
      typeof categoryData.postStats[0].likes === 'number' ? categoryData.postStats[0].likes : 0
    )
  );
  const baseShareCount = Math.max(
    ...categories.map(categoryData =>
      typeof categoryData.postStats[0].shares === 'number' ? categoryData.postStats[0].shares : 0
    )
  );
  const baseCommentsCount = Math.max(
    ...categories.map(categoryData =>
      typeof categoryData.postStats[0].comments === 'number' ? categoryData.postStats[0].comments : 0
    )
  );

  return categories.map(category => {
    const { likes, shares, comments, postCount } = category.postStats[0];

    return {
      categoryId: category.categoryId,
      postCount: postCount,
      postPercentage: (100 * postCount) / basePostCount,
      likeCount: likes,
      likePercentage: (100 * (likes ?? 0)) / (baseLikeCount ?? 1),
      shareCount: shares,
      sharePercentage: (100 * (shares ?? 0)) / (baseShareCount ?? 1),
      commentsCount: comments,
      commentsPercentage: (100 * (comments ?? 0)) / (baseCommentsCount ?? 1)
    };
  });
};

const addCategoriesData = (categoriesData: CategoryData[], userCategories: CategoriesResult): CategoriesUsageData[] => {
  return categoriesData.map(categoryLine => {
    const matchedCategory = userCategories?.find(category => category.id === categoryLine.categoryId);
    if (!matchedCategory) {
      return { ...categoryLine, name: '', color: 'LIGHT_GREY' }; // It should not reach here
    }
    const { name, color } = matchedCategory;
    return {
      ...categoryLine,
      name,
      color
    };
  });
};

export const CategoryTableController = ({ data }: CategoryTableControllerProps) => {
  const { socialProfiles, selectedProfilesIds } = useContext(AnalyticsContext);
  const categories = data?.workspace.categories;
  const categoriesUsage = data?.analyticsPostStats.postStatsByCategory;
  categoriesUsage?.sort((first, second) => second.postStats[0].postCount - first.postStats[0].postCount);
  const categoriesUsageData = addCategoriesData(normalizedCategories(categoriesUsage?.slice(0, 10)), categories);

  const selectedSocialProfiles = socialProfiles.filter(profile => selectedProfilesIds.includes(profile.id));
  const availableMetrics = getAvailableMetrics(selectedSocialProfiles);
  const isEmpty =
    selectedProfilesIds.length === 0 ||
    categoriesUsageData.length === 0 ||
    categoriesUsageData.every(categoryData => categoryData.postCount === 0);

  return (
    <CategoryTableView
      categoriesUsageData={categoriesUsageData}
      availableMetrics={availableMetrics}
      isEmpty={isEmpty}></CategoryTableView>
  );
};
